import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import {
  RefreshCw, Plus, BarChart3, Copy, Play, Pause, Square, Archive, Star, Download, Lightbulb, Eye,
  CalendarDays, Crown, AlertTriangle, Clock3, ChevronLeft, ChevronRight,
} from "lucide-react";

const ST_CLS = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  scheduled: "bg-violet-50 text-violet-800 border-violet-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
  paused: "bg-amber-50 text-amber-800 border-amber-300",
  closed: "bg-blue-50 text-blue-800 border-blue-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const EMPTY = {
  question: "", description: "", optionsText: "", category: "", scope: "ontario",
  municipality_id: "", ward: "", source_name: "", source_url: "", source_date: "",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

const ScheduleDialog = ({ poll, onDone }) => {
  const [open, setOpen] = useState(false);
  const [publishAt, setPublishAt] = useState(toLocalInput(poll.publish_at));
  const [closeAt, setCloseAt] = useState(toLocalInput(poll.close_at));
  const [priority, setPriority] = useState(poll.rotation_priority || "normal");
  const save = async (schedule) => {
    try {
      const body = {
        publish_at: publishAt ? new Date(publishAt).toISOString() : null,
        close_at: closeAt ? new Date(closeAt).toISOString() : null,
        rotation_priority: priority,
      };
      if (schedule) body.status = "scheduled";
      // exclude nulls the API treats as "no change"
      Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
      await adminApi.patch(`/admin/polls/${poll.id}`, body);
      toast.success(schedule ? "Poll scheduled" : "Schedule saved");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title="Schedule" data-testid={`schedule-btn-${poll.id}`}><Clock3 className="h-3.5 w-3.5 text-violet-700" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Schedule poll</DialogTitle>
          <DialogDescription className="line-clamp-2">{poll.question}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Publish date/time</Label>
            <Input type="datetime-local" className="mt-1" value={publishAt}
                   onChange={(e) => setPublishAt(e.target.value)} data-testid="schedule-publish-at" />
          </div>
          <div>
            <Label>Close date/time (optional)</Label>
            <Input type="datetime-local" className="mt-1" value={closeAt}
                   onChange={(e) => setCloseAt(e.target.value)} data-testid="schedule-close-at" />
          </div>
          <div>
            <Label>Rotation priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1 bg-white" data-testid="schedule-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Lifecycle: Draft → Scheduled → Live → Closed → Archived. The scheduler runs every 2 minutes.
            Polls are never auto-generated — only what you approve here is published.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save(true)} disabled={!publishAt} data-testid="schedule-confirm">Schedule</Button>
            <Button size="sm" variant="outline" onClick={() => save(false)} data-testid="schedule-save-only">Save times only</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CalendarView = ({ events }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const byDay = {};
  (events || []).forEach((e) => {
    const key = (e.date || "").slice(0, 10);
    (byDay[key] = byDay[key] || []).push(e);
  });
  const first = new Date(month);
  const startDow = first.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const pad = (n) => String(n).padStart(2, "0");
  const label = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  return (
    <Card className="mt-4" data-testid="schedule-calendar">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Button size="sm" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <p className="font-heading font-semibold text-foreground">{label}</p>
          <Button size="sm" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-muted-foreground mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            const key = d ? `${month.getFullYear()}-${pad(month.getMonth() + 1)}-${pad(d)}` : null;
            const evts = key ? byDay[key] || [] : [];
            return (
              <div key={i} className={`min-h-[64px] rounded border p-1 text-left ${d ? "border-slate-200 bg-white" : "border-transparent"}`}>
                {d && <p className="text-[10px] text-muted-foreground">{d}</p>}
                {evts.slice(0, 3).map((e, j) => (
                  <p key={j} className={`text-[9px] truncate rounded px-1 mt-0.5 ${e.type === "publish" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}
                     title={`${e.type}: ${e.question} (${e.area})`}>
                    {e.type === "publish" ? "▶" : "■"} {e.question}
                  </p>
                ))}
                {evts.length > 3 && <p className="text-[9px] text-muted-foreground">+{evts.length - 3} more</p>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const ResultsDialog = ({ poll }) => {
  const [data, setData] = useState(null);
  const load = () => adminApi.get(`/admin/polls/${poll.id}/results`).then((r) => setData(r.data)).catch(() => toast.error("Could not load results"));
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`poll-results-btn-${poll.id}`}><BarChart3 className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{poll.question}</DialogTitle>
          <DialogDescription>Admin view — includes below-threshold data, comments and review signals.</DialogDescription>
        </DialogHeader>
        {!data ? <Skeleton className="h-48" /> : (
          <div className="space-y-4">
            <p className="text-sm">Responses: <span className="font-bold tabular-nums">{data.results.response_count || 0}</span>
              {data.test_response_count > 0 && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[9px]">+{data.test_response_count} TEST (excluded)</Badge>}
            </p>
            <div className="space-y-1.5">
              {(data.results.options || []).map((o) => (
                <div key={o.id} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                  <span>{o.label}</span><span className="tabular-nums text-muted-foreground">{o.count} ({o.percent}%)</span>
                </div>
              ))}
            </div>
            {data.flags.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                {data.flags.map((f, i) => <p key={i} className="text-xs text-amber-900">{f.detail}</p>)}
              </div>
            )}
            {data.comments.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Anonymous "why" comments ({data.comments.length})</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {data.comments.map((c, i) => (
                    <p key={i} className="text-xs bg-slate-50 border border-slate-100 rounded p-2">{c.comment}</p>
                  ))}
                </div>
              </div>
            )}
            <Button asChild size="sm" variant="outline" data-testid={`poll-export-${poll.id}`}>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/polls/${poll.id}/export`} target="_blank" rel="noopener noreferrer">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export aggregate CSV
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminPolls() {
  const [rows, setRows] = useState(null);
  const [issues, setIssues] = useState(null);
  const [munis, setMunis] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [createOpen, setCreateOpen] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [fatigue, setFatigue] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/polls").then((r) => setRows(r.data)).catch(() => toast.error("Could not load polls"));
    adminApi.get("/admin/polls-issues").then((r) => setIssues(r.data)).catch(() => {});
    adminApi.get("/admin/polls/schedule-queue").then((r) => setSchedule(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    adminApi.get("/admin/municipalities").then((r) => setMunis(r.data)).catch(() =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/municipalities`).then((r) => r.json()).then(setMunis).catch(() => {}));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/polls/categories`).then((r) => r.json()).then(setCategories).catch(() => {});
  }, [load]);

  const setStatus = async (poll, status) => {
    try {
      await adminApi.patch(`/admin/polls/${poll.id}`, { status });
      toast.success(`Poll ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleFeature = async (poll) => {
    try {
      await adminApi.patch(`/admin/polls/${poll.id}`, { featured: !poll.featured });
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleQotw = async (poll) => {
    try {
      await adminApi.patch(`/admin/polls/${poll.id}`, { question_of_week: !poll.question_of_week });
      toast.success(poll.question_of_week ? "Removed as Question of the Week" : "Set as Question of the Week");
      load();
    } catch { toast.error("Update failed"); }
  };

  const runSchedulerNow = async () => {
    try {
      const r = await adminApi.post("/admin/polls/scheduler/tick");
      toast.success(`Scheduler ran — published ${r.data.published}, closed ${r.data.closed}`);
      load();
    } catch { toast.error("Scheduler tick failed"); }
  };

  // Poll-fatigue safeguard: warn when a similar question ran recently
  useEffect(() => {
    if (!createOpen || !form.category) { setFatigue(null); return; }
    const id = setTimeout(() => {
      adminApi.get("/admin/polls/similar-check", {
        params: { category: form.category,
                  municipality_id: form.scope === "ontario" ? undefined : form.municipality_id || undefined } })
        .then((r) => setFatigue(r.data)).catch(() => setFatigue(null));
    }, 300);
    return () => clearTimeout(id);
  }, [createOpen, form.category, form.scope, form.municipality_id]);

  const duplicate = async (poll) => {
    try {
      await adminApi.post(`/admin/polls/${poll.id}/duplicate`);
      toast.success("Duplicated as draft");
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  const createPoll = async (e) => {
    e.preventDefault();
    const options = form.optionsText.split("\n").map((s) => s.trim()).filter(Boolean).map((label) => ({ label }));
    if (options.length < 2) { toast.error("Provide at least 2 options (one per line)"); return; }
    try {
      await adminApi.post("/admin/polls", {
        question: form.question, description: form.description || null, options,
        category: form.category, scope: form.scope,
        municipality_id: form.scope === "ontario" ? null : form.municipality_id || null,
        ward: form.scope === "ward" ? form.ward || null : null,
        source_name: form.source_name || null, source_url: form.source_url || null,
        source_date: form.source_date || null,
      });
      toast.success("Draft poll created — review then publish");
      setForm(EMPTY); setCreateOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const issueAction = async (issue, action) => {
    try {
      if (action === "create-poll") {
        const r = await adminApi.post(`/admin/polls-issues/${issue.id}/create-poll`);
        toast.success(r.data.note || "Draft poll created");
      } else {
        await adminApi.patch(`/admin/polls-issues/${issue.id}`, { status: action });
        toast.success(`Issue ${action}`);
      }
      load();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-polls-title">Community Polls</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Neutral civic listening — every question is admin-reviewed. Results are threshold-protected and never linked to declarations.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="polls-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="create-poll-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Create Poll</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Community Poll</DialogTitle>
                <DialogDescription>Keep it neutral, balanced and easy to understand. Created as a draft.</DialogDescription>
              </DialogHeader>
              <form onSubmit={createPoll} className="space-y-3">
                <div>
                  <Label>Question</Label>
                  <Textarea className="mt-1" rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="new-poll-question" />
                </div>
                <div>
                  <Label>Answer options (one per line)</Label>
                  <Textarea className="mt-1" rows={5} value={form.optionsText} onChange={(e) => setForm({ ...form, optionsText: e.target.value })} data-testid="new-poll-options" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="new-poll-category"><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent className="bg-white">{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scope</Label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="new-poll-scope"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="ontario">Ontario-wide</SelectItem>
                        <SelectItem value="municipality">Municipality</SelectItem>
                        <SelectItem value="ward">Ward</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.scope !== "ontario" && (
                  <div>
                    <Label>Municipality</Label>
                    <Select value={form.municipality_id || undefined} onValueChange={(v) => setForm({ ...form, municipality_id: v })}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="new-poll-muni"><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent className="bg-white">{munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {form.scope === "ward" && (
                  <div><Label>Ward</Label><Input className="mt-1" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} data-testid="new-poll-ward" /></div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Source name</Label><Input className="mt-1" value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} data-testid="new-poll-source-name" /></div>
                  <div><Label>Source URL</Label><Input className="mt-1" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="new-poll-source-url" /></div>
                </div>
                {fatigue?.has_warning && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="fatigue-warning">
                    {fatigue.warnings.slice(0, 2).map((w, i) => (
                      <p key={i} className="text-xs text-amber-900 inline-flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>⚠ {w.message} ({w.match}: “{w.question.slice(0, 60)}…”)</span>
                      </p>
                    ))}
                    <p className="text-[10px] text-amber-800/90 mt-1.5">
                      Rotation: {fatigue.rotation_order.join(" → ")}. {fatigue.rotation_hint}
                    </p>
                  </div>
                )}
                {fatigue && !fatigue.has_warning && form.category && (
                  <p className="text-[11px] text-emerald-700" data-testid="fatigue-clear">
                    No similar question in the last 7 days. {fatigue.rotation_hint}
                  </p>
                )}
                <Button type="submit" className="w-full" data-testid="new-poll-submit">Create Draft</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="polls">
        <TabsList>
          <TabsTrigger value="polls" data-testid="tab-polls">Polls</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Suggested Local Issues</TabsTrigger>
        </TabsList>

        <TabsContent value="schedule">
          <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">
              Draft → Scheduled → Live → Closed → Archived. Transitions run automatically every 2 minutes.
            </p>
            <Button size="sm" variant="outline" onClick={runSchedulerNow} data-testid="run-scheduler-now">
              <Clock3 className="h-3.5 w-3.5 mr-1.5" /> Run scheduler now
            </Button>
          </div>
          {!schedule ? <Skeleton className="h-40 mt-4" /> : (
            <>
              <Card className="mt-3"><CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Publish</TableHead><TableHead>Close</TableHead><TableHead>Poll</TableHead>
                    <TableHead>Area</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody data-testid="schedule-queue-table">
                    {schedule.queue.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground text-center py-6">
                        Nothing scheduled yet — use the clock icon on any poll to schedule it.
                      </TableCell></TableRow>
                    )}
                    {schedule.queue.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-xs tabular-nums">{p.publish_at ? new Date(p.publish_at).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-xs tabular-nums">{p.close_at ? new Date(p.close_at).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-xs max-w-[240px] truncate">{p.question}</TableCell>
                        <TableCell className="text-xs">{p.area}</TableCell>
                        <TableCell className="text-xs">{p.rotation_priority === "high" ? <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px]">high</Badge> : "normal"}</TableCell>
                        <TableCell><Badge variant="outline" className={`${ST_CLS[p.status] || ""} text-[10px]`}>{p.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
              <CalendarView events={schedule.events} />
            </>
          )}
        </TabsContent>

        <TabsContent value="polls">
          {!rows ? <Skeleton className="h-64 mt-4" /> : (
            <Card className="mt-4"><CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Question</TableHead><TableHead>Area</TableHead><TableHead>Category</TableHead>
                  <TableHead>Status</TableHead><TableHead>Responses</TableHead><TableHead>Source</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody data-testid="admin-polls-table">
                  {rows.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="max-w-[260px]">
                        <p className="text-sm font-medium truncate">{p.question}</p>
                        {p.featured && <Badge variant="outline" className="text-[9px] mt-1 bg-blue-50 text-blue-800 border-blue-200">Featured</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{p.scope === "ontario" ? "Ontario" : `${p.municipality_name || ""}${p.ward ? ` · ${p.ward}` : ""}`}</TableCell>
                      <TableCell className="text-xs">{(p.category || "").replace(/_/g, " ")}</TableCell>
                      <TableCell><Badge variant="outline" className={`${ST_CLS[p.status] || ""} text-[10px]`} data-testid={`poll-status-${p.id}`}>{p.status}</Badge></TableCell>
                      <TableCell className="tabular-nums text-sm">{p.response_count}{p.test_response_count > 0 && <span className="text-[10px] text-amber-700"> (+{p.test_response_count} test)</span>}</TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate">{p.source?.name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-0.5">
                          {p.status !== "published" && p.status !== "archived" && (
                            <Button size="sm" variant="ghost" title="Publish" onClick={() => setStatus(p, "published")} data-testid={`publish-${p.id}`}><Play className="h-3.5 w-3.5 text-emerald-700" /></Button>
                          )}
                          {p.status === "published" && (
                            <Button size="sm" variant="ghost" title="Pause" onClick={() => setStatus(p, "paused")} data-testid={`pause-${p.id}`}><Pause className="h-3.5 w-3.5 text-amber-700" /></Button>
                          )}
                          {["published", "paused"].includes(p.status) && (
                            <Button size="sm" variant="ghost" title="Close" onClick={() => setStatus(p, "closed")} data-testid={`close-${p.id}`}><Square className="h-3.5 w-3.5" /></Button>
                          )}
                          {p.status !== "archived" && (
                            <Button size="sm" variant="ghost" title="Archive" onClick={() => setStatus(p, "archived")} data-testid={`archive-${p.id}`}><Archive className="h-3.5 w-3.5" /></Button>
                          )}
                          <Button size="sm" variant="ghost" title="Feature on homepage" onClick={() => toggleFeature(p)} data-testid={`feature-${p.id}`}>
                            <Star className={`h-3.5 w-3.5 ${p.featured ? "text-amber-500 fill-amber-400" : ""}`} />
                          </Button>
                          <Button size="sm" variant="ghost" title="Question of the Week" onClick={() => toggleQotw(p)} data-testid={`qotw-${p.id}`}>
                            <Crown className={`h-3.5 w-3.5 ${p.question_of_week ? "text-violet-600 fill-violet-300" : ""}`} />
                          </Button>
                          <ScheduleDialog poll={p} onDone={load} />
                          <Button size="sm" variant="ghost" title="Duplicate" onClick={() => duplicate(p)} data-testid={`duplicate-${p.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                          <ResultsDialog poll={p} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="issues">
          {!issues ? <Skeleton className="h-48 mt-4" /> : (
            <div className="space-y-3 mt-4" data-testid="issues-list">
              {issues.map((i) => (
                <Card key={i.id}>
                  <CardContent className="p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Lightbulb className="h-4 w-4 text-amber-600 shrink-0" />
                        <p className="font-medium text-sm text-foreground">{i.title}</p>
                        <Badge variant="outline" className="text-[9px]">{i.status}</Badge>
                        <Badge variant="outline" className="text-[9px] bg-secondary">{i.origin.replace(/_/g, " ")}</Badge>
                      </div>
                      {i.description && <p className="text-xs text-muted-foreground mt-1">{i.description}</p>}
                      {i.suggested_question && (
                        <p className="text-xs text-foreground mt-1 italic" data-testid={`issue-suggested-question-${i.id}`}>
                          Suggested wording: “{i.suggested_question}”{i.ward ? ` · ${i.ward}` : ""}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {i.municipality_name || "Ontario"} · {i.category ? i.category.replace(/_/g, " ") : "uncategorized"} ·
                        source: {i.source_name || "—"} {i.source_url && <a href={i.source_url} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">(link)</a>}
                        {i.source_date && ` · ${i.source_date}`}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {i.status !== "polled" && (
                        <Button size="sm" onClick={() => issueAction(i, "create-poll")} data-testid={`issue-create-poll-${i.id}`}>Create Poll</Button>
                      )}
                      {i.status === "suggested" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => issueAction(i, "approved")} data-testid={`issue-approve-${i.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => issueAction(i, "research")} data-testid={`issue-research-${i.id}`}>Research Further</Button>
                          <Button size="sm" variant="ghost" onClick={() => issueAction(i, "dismissed")} data-testid={`issue-dismiss-${i.id}`}>Dismiss</Button>
                        </>
                      )}
                      {i.status === "research" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => issueAction(i, "approved")} data-testid={`issue-approve-${i.id}`}>Approve</Button>
                          <Button size="sm" variant="ghost" onClick={() => issueAction(i, "dismissed")} data-testid={`issue-dismiss-${i.id}`}>Dismiss</Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {issues.length === 0 && <p className="text-sm text-muted-foreground mt-4">No suggested issues.</p>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
